import React, { useState } from 'react';
import { OrderUploadingProps, OrderUploadingTypes } from './order-uploading.types';
import InfoHint from '../../../components/info-hint/info-hint-view';
import { InfoHintType } from '../../../components/info-hint/info-hint.props';
import UploadFile from '../../../components/setup-assistant/upload-document/upload-file';
import { Button, Flex, Text } from '../../../common/common-components.styled';
import { MimeTypes } from '../../../common/enums';
import { SaveIcon } from '../../../components/icons-new/save';
import { useFetch } from '../../../hooks/use-fetch/use-fetch';
import { fileProviderRequestConfig } from '../../../request-config/file-provider/file-provider.request-config';
import { createEvent, updateLeasingOffer } from '../../../services/fleet-vehicle-service';
import { AnyObject, ResponseModel } from '../../../common/interfaces';
import { crmDataTypes, RequestStatuses } from '../../../constants';
import { getRequestItem } from '../../../utils/get-response-data';
import { AxiosResponse } from 'axios';
import { useOverlay } from '../../../hooks';
import useRequestSnackbar from '../../../hooks/use-request-snackbar';
import { MAX_FILE_SIZE, ORDER_UPLOADING_DATA } from './utils';
import { getEventsByType, takeLatestEvent } from '../../../utils/offer-events-functions';
import { sharePointRequestConfig } from '../../../request-config/sharepoint/sharepoint.request-config';

const OrderUploading = ({
  vehicleOfferId,
  refreshData,
  events,
  type,
  activeLeasingOffer,
  noNeedToSign
}: OrderUploadingProps) => {
  const [showOverlay, hideOverlay] = useOverlay('unsignedContractLoadingKey');

  const [files, setFiles] = useState<File[]>([]);
  const [uploadNewFile, setUploadNewFile] = useState(false);

  const { fetch } = useFetch({ isShowLoading: false, isShowSuccessSnackbar: false });
  const { showRequestSnackbar } = useRequestSnackbar();

  const eventByOrderUploadingType = takeLatestEvent(
    getEventsByType(events, ORDER_UPLOADING_DATA[type].eventDescription)
  );

  const isFileUploaded = !!eventByOrderUploadingType;

  const save = async () => {
    if (!vehicleOfferId) {
      return;
    }
    showOverlay();
    let response: PromiseSettledResult<AxiosResponse<ResponseModel>>[] | null = null;
    let requests: Promise<any>[] = [];
    const uploadFilesDataToSend = {
      files,
      logicalName: 'uds_fahrzeugangebote',
      entityId: vehicleOfferId,
      documentTypeId: ORDER_UPLOADING_DATA[type].documentType.id
    };
    const createEventData: AnyObject = {};
    if (uploadNewFile) {
      const response = await fetch({
        requestConfig: sharePointRequestConfig.getFilesByDescriptions({
          entityId: vehicleOfferId,
          descriptions: [ORDER_UPLOADING_DATA[type].documentType.name]
        })
      });
      const fileId = response?.data?.data[0]?.id || '';

      const createEventDataFields = [
        { name: 'uds_fahrzeugangebotid', value: vehicleOfferId, attributeTypeCode: crmDataTypes.Lookup },
        {
          name: 'uds_eventdescription',
          value: ORDER_UPLOADING_DATA[type].actualizeFileEventDescription.id,
          attributeTypeCode: crmDataTypes.Lookup
        }
      ];
      createEventDataFields.forEach(field => {
        createEventData[field.name] = getRequestItem(field.value, field.attributeTypeCode);
      });

      requests = [
        fetch({
          requestConfig: fileProviderRequestConfig.moveToRecycleBin('uds_fahrzeugangebote', fileId),
          callback: refreshData
        }),
        fetch({ requestConfig: fileProviderRequestConfig.uploadFiles(uploadFilesDataToSend), callback: refreshData })
      ];
    } else {
      const createEventDataFields = [
        { name: 'uds_fahrzeugangebotid', value: vehicleOfferId, attributeTypeCode: crmDataTypes.Lookup },
        {
          name: 'uds_eventdescription',
          value: ORDER_UPLOADING_DATA[type].eventDescription.id,
          attributeTypeCode: crmDataTypes.Lookup
        }
      ];
      createEventDataFields.forEach(field => {
        createEventData[field.name] = getRequestItem(field.value, field.attributeTypeCode);
      });

      requests = [
        fetch({ requestConfig: fileProviderRequestConfig.uploadFiles(uploadFilesDataToSend), callback: refreshData })
      ];

      const addUpdateLeasingOfferRequest = (contractstatecodeValue: number) => {
        const requestData: AnyObject = {};

        requestData.uds_leasingangeboteid = getRequestItem(
          activeLeasingOffer?.uds_leasingangeboteid,
          crmDataTypes.Uniqueidentifier
        );
        requestData.uds_contractstatecode = getRequestItem(contractstatecodeValue, crmDataTypes.Picklist);
        requests.push(updateLeasingOffer(requestData));
      };
      if (type === OrderUploadingTypes.SignedOrder && activeLeasingOffer?.uds_leasingangeboteid) {
        addUpdateLeasingOfferRequest(100000000); // Offen
      }
      if (type === OrderUploadingTypes.UnsignedLeasingContract && activeLeasingOffer?.uds_leasingangeboteid) {
        addUpdateLeasingOfferRequest(100000001); // In Bearbeitung
      }
    }

    const sendNoNeedToSignEvent = () => {
      const createNoNeedToSignEventData: AnyObject = {};
      const noNeedToSignEventDataFields = [
        { name: 'uds_fahrzeugangebotid', value: vehicleOfferId, attributeTypeCode: crmDataTypes.Lookup },
        {
          name: 'uds_eventdescription',
          value: ORDER_UPLOADING_DATA[type].noNeedToSignEventDescription?.id,
          attributeTypeCode: crmDataTypes.Lookup
        }
      ];
      noNeedToSignEventDataFields.forEach(field => {
        createNoNeedToSignEventData[field.name] = getRequestItem(field.value, field.attributeTypeCode);
      });
      createEvent(createNoNeedToSignEventData);
    };
    try {
      createEvent(createEventData).then(response => {
        if (
          response.data?.level === RequestStatuses.Success &&
          noNeedToSign &&
          !uploadNewFile &&
          ORDER_UPLOADING_DATA[type].noNeedToSignEventDescription
        ) {
          sendNoNeedToSignEvent();
        }
      });
      response = await Promise.allSettled(requests);
    } catch (e) {
      console.log(e);
    } finally {
      hideOverlay();
    }

    showRequestSnackbar({
      successCondition: !!response?.every(item => {
        if (item.status === 'fulfilled') {
          return item.value?.data?.level === RequestStatuses.Success || item.value?.status === 200;
        }
        return false;
      }),
      successCallback: () => {
        refreshData();
        setUploadNewFile(false);
      }
    });
  };

  return (
    <div>
      {isFileUploaded && (
        <Text bottom='20' color='darkBlue'>
          {ORDER_UPLOADING_DATA[type].uploadedDate(eventByOrderUploadingType?.createdon.value)}
        </Text>
      )}

      <InfoHint type={InfoHintType.Info}>
        {isFileUploaded
          ? 'Entferne dieses Dokument, wenn Du es durch ein neueres ersetzen möchtest.'
          : ORDER_UPLOADING_DATA[type].infoHint}
      </InfoHint>

      {(!isFileUploaded || uploadNewFile) && (
        <>
          <Text top='50' bottom='10' color='darkBlue'>
            {ORDER_UPLOADING_DATA[type].uploadingComponentLabel}
            <Text left='5' as='span' color='pink'>
              *
            </Text>
          </Text>
          <UploadFile
            onDropAccepted={files => setFiles(files)}
            accept={[MimeTypes.PDF]}
            acceptString={'pdf'}
            multiple={false}
            maxSize={MAX_FILE_SIZE}
          />
        </>
      )}

      <Flex top='40' justify='flex-end'>
        {isFileUploaded && !uploadNewFile ? (
          <Button onClick={() => setUploadNewFile(true)} secondary>
            Aktualisiertes Dokument Hochladen
          </Button>
        ) : (
          <>
            {uploadNewFile && (
              <Button right='10' onClick={() => setUploadNewFile(false)} secondary>
                Abbrechen
              </Button>
            )}
            <Button disabled={!files.length} onClick={save}>
              <Flex align='flex-start'>
                <SaveIcon width='20' height='20' color='white' />
                <Text left='5' as='span'>
                  Speichern
                </Text>
              </Flex>
            </Button>
          </>
        )}
      </Flex>
    </div>
  );
};

export default OrderUploading;
